import React, { useEffect, useRef, useState } from 'react';
import { Fab, Zoom } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const ScrollToTop: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);

  // ✅ Moved getScrollableParent outside useEffect so it's accessible everywhere
  const getScrollableParent = (
    element: HTMLElement | null
  ): HTMLElement | Window => {
    if (!element) return window;
    const overflowY = window.getComputedStyle(element).overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }
    return element.parentElement
      ? getScrollableParent(element.parentElement)
      : window;
  };

  useEffect(() => {
    const scrollableParent = getScrollableParent(fabRef.current);

    const handleScroll = () => {
      const scrollPos =
        scrollableParent === window
          ? window.scrollY
          : (scrollableParent as HTMLElement).scrollTop;
      setVisible(scrollPos >= 400);
    };

    scrollableParent.addEventListener('scroll', handleScroll);
    return () => scrollableParent.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    const scrollableParent = fabRef.current
      ? getScrollableParent(fabRef.current)
      : window;

    if (scrollableParent === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      (scrollableParent as HTMLElement).scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Zoom in={visible}>
      <Fab
        ref={fabRef}
        onClick={handleClick}
        color="primary"
        size="medium"
        sx={{
          position: 'fixed',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999
        }}
        aria-label="scroll back to top"
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Zoom>
  );
};

export default ScrollToTop;
